import { Shaliach, StamOrders, type HebrewDefiniteArticle, type PersonalPageItemLabels } from '@sm-dashboard/types';

export const formatDate = (dateString: string | Date, isDateOnly = false, isTimeOnly = false) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const formattedDate = `${day}/${month}/${date.getFullYear()}`;
  const formattedTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

  return isDateOnly ? formattedDate : isTimeOnly ? formattedTime : `${formattedDate} ${formattedTime}`;
};

export const formatShaliachName = (data: Shaliach | StamOrders | undefined) => {
  if (!data) return '';
  return `${(data as Shaliach)?.nickName || data?.firstName || ''} ${data?.lastName || ''}`;
};

export const formatShaliachBranch = (shaliach: Shaliach | undefined) => {
  if (!shaliach) return '';

  if (shaliach?.CwCenter) {
    return `${shaliach.CwCenter.title || ''} ${
      shaliach.CwCenter.addressLine1 ? `- ${shaliach.CwCenter.addressLine1}` : ''
    }`;
  } else if (shaliach?.Branch) {
    return `${shaliach.Branch.branchName || ''} ${
      shaliach.Branch.subBranchName ? `- ${shaliach.Branch.subBranchName}` : ''
    }`;
  }

  return '';
};

const HEBREW_VAV = 'ו';
const HEBREW_HE = 'ה';

function applyHebrewDefiniteArticleToWord(word: string): string {
  if (!word) return word;
  if (word.startsWith(HEBREW_VAV)) {
    return `${HEBREW_VAV}${HEBREW_HE}${word.slice(1)}`;
  }
  return `${HEBREW_HE}${word}`;
}

function getHebrewDefiniteArticleIndexes(wordCount: number, definiteArticle: HebrewDefiniteArticle): number[] {
  if (Array.isArray(definiteArticle)) {
    return definiteArticle.filter(index => index >= 0 && index < wordCount);
  }

  switch (definiteArticle) {
    case 'all':
      return Array.from({ length: wordCount }, (_, index) => index);
    case 'firstExceptLast':
      return wordCount <= 1 ? [] : Array.from({ length: wordCount - 1 }, (_, index) => index);
    case 'lastExceptFirst':
      return wordCount <= 1 ? [] : Array.from({ length: wordCount - 1 }, (_, index) => index + 1);
    default:
      return [];
  }
}

export const addHebrewDefiniteArticle = (label: string, definiteArticle?: HebrewDefiniteArticle | boolean): string => {
  if (!label) return label;
  const words = label.split(' ');

  if (definiteArticle === true) {
    if (words.length === 1) {
      return label;
    }
    const indexes = getHebrewDefiniteArticleIndexes(words.length, 'lastExceptFirst');
    return words
      .map((word, index) => (indexes.includes(index) ? applyHebrewDefiniteArticleToWord(word) : word))
      .join(' ');
  }

  if (definiteArticle === false) {
    if (words.length === 1) {
      return applyHebrewDefiniteArticleToWord(label);
    }
    words[1] = applyHebrewDefiniteArticleToWord(words[1]);
    return words.join(' ');
  }

  const mode = definiteArticle ?? (words.length === 1 ? 'all' : ([1] as const));
  const indexes = getHebrewDefiniteArticleIndexes(words.length, mode);

  return words
    .map((word, index) => (indexes.includes(index) ? applyHebrewDefiniteArticleToWord(word) : word))
    .join(' ');
};

/**
 * Format date in Hebrew
 */
export const formatHebrewDate = (date: Date): string => {
  return new Intl.DateTimeFormat('he-IL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

/**
 * Normalizes a URL by ensuring it has a protocol.
 * If the URL already has http:// or https://, returns it as-is.
 * Otherwise, adds http:// for localhost, https:// for other domains.
 */
export const normalizeUrl = (url: string | undefined, defaultUrl?: string): string => {
  const baseUrl = url || defaultUrl || '';

  if (!baseUrl) {
    throw new Error('URL is required');
  }

  if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
    return baseUrl;
  }

  const protocol = baseUrl.includes('localhost') ? 'http' : 'https';
  return `${protocol}://${baseUrl}`;
};

export function getCampaignGroupInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return '?';
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = [...parts[0]][0];
    const second = [...parts[1]][0];
    if (first && second) {
      return `${first}${second}`;
    }
  }
  const word = parts[0] ?? trimmed;
  const chars = [...word];
  if (chars.length >= 2) {
    return `${chars[0]}${chars[1]}`;
  }
  return chars[0] ?? '?';
}
